import { Film, Loader2 } from 'lucide-react'
import type { UploadForCutProgress } from '@/api/endpoints/videoCutter'
import { cn } from '@/lib/cn'
import { etaLabel, formatBytes } from '../format'
import styles from '../styles/shell.module.scss'

export interface UploadProgressCardProps {
  /** Original file name — the user may have picked the wrong file and
   * this is their only chance to notice before the editor opens. */
  filename: string
  /** ``null`` until the first progress event lands. */
  progress: UploadForCutProgress | null
  /** ``Date.now()`` when the upload started; feeds the rate estimate. */
  startedAt: number
  /** Source codec when the file is being re-encoded — worth naming, since
   * "why is my upload busy for six minutes" has a concrete answer. */
  fromCodec?: string | null
}

/**
 * Replaces the dropzone while a file is going up. Two visual states,
 * because the wait has two halves: a real percentage while bytes are on
 * the wire, then an indeterminate bar during the server-side probe and
 * faststart remux, which report nothing back.
 */
export function UploadProgressCard({
  filename,
  progress,
  startedAt,
  fromCodec = null,
}: UploadProgressCardProps) {
  const phase = progress?.phase ?? 'uploading'
  const percent = progress?.percent ?? 0
  const sending = phase === 'uploading'
  // Both the upload and the conversion report a real percentage; only the
  // probe/remux window in between has nothing to show.
  const determinate = phase !== 'processing'

  // Average rate over the whole upload, not an instantaneous one: the
  // per-event deltas are noisy enough to make the ETA jump around.
  const elapsed = (Date.now() - startedAt) / 1000
  const rate = progress && elapsed > 1 ? progress.loaded / elapsed : 0
  const eta = progress && rate > 0 ? etaLabel((progress.total - progress.loaded) / rate) : null

  const sentLabel = progress
    ? [
        [formatBytes(progress.loaded) ?? '0 KB', formatBytes(progress.total)].filter(Boolean).join(' de '),
        rate > 0 ? `${formatBytes(rate) ?? ''}/s` : null,
        eta,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Preparando envio...'

  return (
    <div className={styles.progressCard} aria-live="polite">
      <div className={styles.progressHeader}>
        {sending ? (
          <Film className={styles.progressIcon} aria-hidden />
        ) : (
          <Loader2 className={cn(styles.progressIcon, styles.spin)} aria-hidden />
        )}
        <span className={styles.progressMsg}>{filename}</span>
        {determinate ? <span className={styles.progressPct}>{percent}%</span> : null}
      </div>
      <div
        className={styles.progressBar}
        role="progressbar"
        aria-label={sending ? 'Progresso do upload' : 'Progresso da conversão'}
        // Omitted while the server works — an indeterminate bar is exactly
        // what a progressbar with no aria-valuenow means.
        aria-valuenow={determinate ? percent : undefined}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={cn(styles.progressFill, !determinate && styles.progressFillPending)}
          style={determinate ? { width: `${percent}%` } : undefined}
        />
      </div>
      <span className={styles.progressSub}>
        {sending
          ? sentLabel
          : phase === 'converting'
            ? `Convertendo para H.264${fromCodec ? ` (o vídeo veio em ${fromCodec.toUpperCase()}, que o navegador não exibe)` : ''} — pode levar alguns minutos`
            : 'Processando no servidor (preparando para reprodução)...'}
      </span>
    </div>
  )
}
